import React from "react";

import { css, cx } from "@emotion/css";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { usePatientIdFromURL } from "@/hooks/usePatientIdFromURL.ts";
import { useStyles } from "@/hooks/useStyles.ts";
import { Queries } from "@/queries/Queries.ts";
import { Form } from "@/uiKit/atoms/forms/Form.tsx";
import { TextToast } from "@/uiKit/atoms/Toast.tsx";
import { MessageAttachments } from "@/uiKit/molecules/chat/MessageAttachments.tsx";
import { MessageData, messageDataSchema } from "@/uiKit/molecules/chat/MessageDataSchema.ts";
import { MessageTextBox } from "@/uiKit/molecules/chat/MessageTextBox.tsx";

export const SEND_MESSAGE_FORM_ID = "SEND_MESSAGE_FORM";

export type SendMessageFormProps = {
  onSendMessage?: () => void;
  className?: string;
};

export const SendMessageForm: React.FC<SendMessageFormProps> = ({ onSendMessage, className }) => {
  const styles = useStyles(makeStyles);
  const { t } = useTranslation();
  const me = Queries.user.usePractitionerMe({});
  const sendMessage = Queries.messaging.useCreateMessage();
  const queryClient = useQueryClient();
  const patientId = usePatientIdFromURL();
  const draftMessage = Queries.messaging.useDraftMessage({
    patientId,
    practitionerId: me.id,
  });
  const updateDraftMessage = Queries.messaging.useUpdateDraftMessage();
  const isBillable = Queries.condition.useCondition(patientId, {
    select: data => data.isBillable,
  });

  const form = useForm<MessageData>({
    defaultValues: {
      // If the patient is not billable, there is no nurse, so the practitioner tab is disabled
      visibility: isBillable ? "practitioners" : "all",
      content_text: draftMessage.message ?? "",
      attachments: [],
    },
    resolver: zodResolver(messageDataSchema),
    criteriaMode: "all",
    disabled: sendMessage.isPending,
  });

  return (
    <Form
      id={SEND_MESSAGE_FORM_ID}
      className={cx(styles.container, className)}
      onSubmit={({ attachments, ...data }) => {
        const files = (attachments ?? []).map(attachment => ({
          file: attachment,
          name: attachment.name,
        }));
        sendMessage.mutate(
          {
            patientId,
            ...data,
            attachments: files,
          },
          {
            onSuccess: () => {
              form.reset({
                visibility: data.visibility,
                content_text: "",
                attachments: [],
              });
              updateDraftMessage.mutate({
                patientId,
                practitionerId: me.id,
                message: undefined,
              });
              onSendMessage && onSendMessage();
            },
          },
        );
      }}
      onError={e => {
        if (e.attachments?.message) {
          TextToast.error(t(e.attachments.message));
        }
      }}
      formContext={form}
    >
      <div
        onMouseEnter={() =>
          Queries.messaging.useMessages.invalidate(queryClient, {
            patientId,
          })
        }
      >
        <MessageAttachments />
        <MessageTextBox />
      </div>
    </Form>
  );
};

const makeStyles = () => ({
  container: css({
    width: "100%",
  }),
});
